import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NbAuthService } from '@nebular/auth';
import { NbWindowRef, NbWindowService, NbWindowState } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ServicesHealthCheck } from '../../models/services-health-check.model';
import { HealthCheckService } from '../../services/health-check.service';

@Component({
  selector: 'def-services-health-check',
  templateUrl: './def-services-health-check.component.html',
  styleUrls: ['./def-services-health-check.component.scss'],
})
export class DefServicesHealthCheckComponent implements AfterViewInit, OnInit {

  @ViewChild('window') windowTemplate: TemplateRef<any>;

  servicesOk: boolean;
  subscription: Subscription;
  windowRef: NbWindowRef;
  isUserAuthenticated: boolean;

  private readonly refreshInterval = 120000;

  constructor(private healthCheckService: HealthCheckService,
              private windowService: NbWindowService,
              private authService: NbAuthService,
              private translateService: TranslateService) {
  }

  /**
   * Check if environment is production
   */
  private static isPreprodEnvironment(): boolean {
    return environment.envName === 'preprod';
  }

  /**
   * Returns whether services are OK
   *
   * @param healthCheck The health check of services as run by the BFF
   */
  private static areServicesOk(healthCheck: ServicesHealthCheck): boolean {
    for (const service in healthCheck) {
      if (healthCheck[service] !== true) {
        return false;
      }
    }

    return true;
  }

  ngAfterViewInit(): void {
    this.authService.isAuthenticated().subscribe(
      () => {
        if (DefServicesHealthCheckComponent.isPreprodEnvironment()) {
          this.initRefreshCycle();
          this.initWindow().subscribe(
            result => this.windowRef = result,
          );
        }
      },
    );
  }

  ngOnInit(): void {
    this.watchAuthenticationChanges();
  }

  /**
   * Watches for authentication changes and updates variables that stores if user is logged in
   */
  private watchAuthenticationChanges(): void {
    this.authService.onAuthenticationChange()
      .subscribe(
        (res) => this.isUserAuthenticated = res,
      );
  }

  /**
   * Inits the external service check refresh cycle
   */
  private initRefreshCycle() {
    this.refreshServicesStatus();
    setInterval(() => {
      this.refreshServicesStatus();
    }, this.refreshInterval);
  }

  /**
   * Inits the window component that contains the services check
   */
  private initWindow(): Observable<NbWindowRef> {
    return this.translateService.get('HEALTHCHECK.EXTERNALSERVICES.WINDOWTITLE').pipe(
      map(title => this.windowService.open(this.windowTemplate,
          {
            title,
            hasBackdrop: true,
            closeOnEsc: false,
            closeOnBackdropClick: false,
            initialState: NbWindowState.MAXIMIZED,
          })),
    );
  }

  /**
   * Refreshes status of external services
   */
  private refreshServicesStatus(): void {
    this.cancelPreviousSubscription();
    this.subscription = this.getServicesStatus();
  }

  /**
   * Changes display mode (maximized, minimized, full screen) of window depending on health checks
   */
  private refreshWindowState(): void {
    if (this.servicesOk) {
      if (this.windowRef.state === NbWindowState.FULL_SCREEN) {
        this.windowRef.maximize();
      }
    } else {
      this.windowRef.fullScreen();
    }
  }

  /**
   * Fetches status of external services if user is authenticated
   */
  private getServicesStatus(): Subscription {
    if (this.isUserAuthenticated) {
      return this.healthCheckService
        .externalServices()
        .subscribe(
          result => {
            this.servicesOk = DefServicesHealthCheckComponent.areServicesOk(result);
            this.refreshWindowState();
          },
          () => {
            this.servicesOk = false;
            this.refreshWindowState();
          },
        );
    }
  }

  /**
   * Cancels previous subscription to external services health check if needed
   */
  private cancelPreviousSubscription(): void {
    if (this.subscription !== undefined) {
      this.subscription.unsubscribe();
    }
  }
}
